import CryptoJS from 'crypto-js'
import { parseUnits } from 'viem'

import {
  ADDRESS_ZERO,
  POPULAR_TOKENS,
  WAREHOUSE_NATIVE_TOKEN,
} from '~/constants/addresses'
import { SupportedChainId } from '~/constants/chains'
import SPLITS_ADDITIONAL_TOKENS from '~/constants/tokenList/SPLITS_ADDITIONAL_TOKEN_LIST.json'
import UNISWAP_TOKENS from '~/constants/tokenList/UNISWAP_DEFAULT_TOKEN_LIST.json'
import { CUSTOM_COLORS, CUSTOM_SYMBOL_OVERRIDES } from '~/constants/tokens'
import { TokenMetadata, TokenMetadataWithPrice } from '~/hooks/useTokenMetadata'
import { BackendTokenDict, IAddress, Multichain } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { isVaultToken } from '~/utils/vault'

type TokenMap = {
  [chainId: string]: {
    [address: string]: {
      symbol: string
      decimals: number
    }
  }
}

// Combine the uniswap default list and splits additional list into a token map by chain
const TOKENS_MAP: TokenMap = UNISWAP_TOKENS.tokens.reduce((acc, tokenData) => {
  const chainId = tokenData.chainId
  if (!acc[chainId]) acc[chainId] = {}
  acc[chainId][tokenData.address] = {
    symbol: tokenData.symbol,
    decimals: tokenData.decimals,
  }

  return acc
}, {} as TokenMap)

SPLITS_ADDITIONAL_TOKENS.tokens.map((tokenData) => {
  const chainId = tokenData.chainId
  if (!TOKENS_MAP[chainId]) TOKENS_MAP[chainId] = {}
  TOKENS_MAP[chainId][tokenData.address] = {
    symbol: tokenData.symbol,
    decimals: tokenData.decimals,
  }
})

const isPopularToken = (token: IAddress, chainId: SupportedChainId) => {
  return POPULAR_TOKENS[chainId]?.includes(EvmAddress.format(token))
}

const INVALID_TOKEN_SYMBOL_CHARACTERS = ['.', ':', '/']

export const shouldDisplayToken = ({
  token,
  chainId,
  tokenMetadata,
  tokenWhitelist,
  hideZeroValue = false,
  value,
}: {
  token: IAddress
  chainId: SupportedChainId
  tokenMetadata: TokenMetadataWithPrice
  tokenWhitelist: Multichain<BackendTokenDict>
  hideZeroValue?: boolean
  value: number | undefined
}): boolean => {
  // Positions are always shown, without waiting on metadata or price, unless
  // their value would display as $0.00.
  if (isVaultToken(chainId, EvmAddress.format(token))) {
    return !isVaultDustValue(value)
  }

  if (hideZeroValue && (!value || isSmallValue(value))) return false

  if (isPopularToken(token, chainId)) return true

  // Anything whitelisted always gets displayed
  if (isWhitelistedToken(token, chainId, tokenWhitelist)) return true

  if (!tokenMetadata || !tokenMetadata.symbol) return false
  if (!tokenMetadata.price) {
    if (tokenMetadata.price === undefined) return false // Still loading
    if (tokenMetadata.price === 0) {
      const isInvalid = INVALID_TOKEN_SYMBOL_CHARACTERS.some((char) =>
        tokenMetadata.symbol.includes(char),
      )
      if (isInvalid) return false
    }
  }

  return true
}

export const isSmallValue = (value: number) => value < 1

const isVaultDustValue = (value: number | undefined) =>
  value !== undefined && value < 0.005

const isWhitelistedToken = (
  token: string,
  chainId: SupportedChainId,
  tokenWhitelist: Multichain<BackendTokenDict>,
): boolean => {
  return tokenWhitelist[chainId]?.[EvmAddress.format(token)] ? true : false
}

export const unwrapToken = (token: IAddress): IAddress => {
  if (token.toLowerCase() === WAREHOUSE_NATIVE_TOKEN.toLowerCase())
    return ADDRESS_ZERO
  return token
}

export const isNativeTokenAddress = (
  address: string | null | undefined,
): boolean => {
  if (!address) return false
  const lower = address.toLowerCase()
  return (
    lower === ADDRESS_ZERO || lower === WAREHOUSE_NATIVE_TOKEN.toLowerCase()
  )
}

// Loose by design — token-shaped objects across the app carry the address under
// either `address` or `token`, and the chain as SupportedChainId or plain number.
type TokenLike = {
  chainId?: number
  address?: string
  token?: string
}

// Same token: same chain + same address. Accepts either `address` or `token`
// for the address field, since token-shaped objects across the app use both.
export const isSameToken = (a?: TokenLike, b?: TokenLike): boolean =>
  a?.chainId === b?.chainId &&
  Address.equal(a?.token ?? a?.address, b?.token ?? b?.address)

export const getTokenColor = (symbol: string) => {
  let color = getCustomColor(symbol)
  if (color) return color

  /// note: used CryptoJS instead of Crypto because edge runtime does not support Crypto.
  const hash = CryptoJS.MD5(symbol).toString(CryptoJS.enc.Hex)

  const r = parseInt(hash.substring(0, 8), 16) % 256
  const g = parseInt(hash.substring(8, 16), 16) % 256
  const b = parseInt(hash.substring(16, 24), 16) % 256

  color = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`

  return color
}

const getCustomColor = (symbol: string) => {
  if (CUSTOM_COLORS[symbol]) {
    return CUSTOM_COLORS[symbol]
  }
  return undefined
}

export const getCustomSymbolOverride = (symbol: string) => {
  if (CUSTOM_SYMBOL_OVERRIDES[symbol]) {
    return CUSTOM_SYMBOL_OVERRIDES[symbol]
  }
  return undefined
}

export const sumStringTokenAmounts = (
  amounts: string[],
  tokenMetadata: TokenMetadata,
) => {
  return amounts.reduce((acc, amount) => {
    if (amount) {
      return acc + parseUnits(amount, tokenMetadata.decimals)
    }
    return acc
  }, BigInt(0))
}
